import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Activity,
  Stethoscope,
  Heart,
  Shield,
  Clock,
  Award,
  ArrowRight,
  CheckCircle
} from 'lucide-react'

const ServicesSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const featuredServices = [
    {
      icon: Activity,
      title: 'Acil Tedavi',
      description: '7/24 acil müdahale hizmeti ile can dostlarınızın her anında yanınızdayız.',
      features: ['24 Saat Hizmet', 'Hızlı Müdahale', 'Uzman Kadro'],
      href: '/hizmetler/acil-tedavi',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: Stethoscope,
      title: 'Kapsamlı Check-Up',
      description: 'Düzenli sağlık kontrolü ile hastalıkları erken teşhis ediyoruz.',
      features: ['Detaylı Muayene', 'Laboratuvar Testleri', 'Aşı Takibi'],
      href: '/hizmetler/check-up',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Heart,
      title: 'Cerrahi Operasyonlar',
      description: 'Modern ameliyathanede güvenli cerrahi müdahaleler gerçekleştiriyoruz.',
      features: ['Modern Ekipman', 'Steril Ortam', 'Anestezi Güvenliği'],
      href: '/hizmetler/cerrahi',
      color: 'from-purple-500 to-purple-600'
    }
  ]

  const clinicFeatures = [
    {
      icon: Shield,
      title: 'Güvenli Ortam',
      description: 'Hijyenik ve steril klinik ortamı'
    },
    {
      icon: Clock,
      title: 'Hızlı Hizmet',
      description: 'Minimum bekleme süresi'
    },
    {
      icon: Award,
      title: 'Uzman Kadro',
      description: 'Deneyimli veteriner hekimler'
    }
  ]

  return (
    <section className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6"
          >
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">Veteriner Hizmetlerimiz</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            Can Dostlarınız İçin
            <span className="text-gradient block">Kapsamlı Hizmetler</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Modern teknoloji ve deneyimli kadromuzla kedi ve köpeklerinizin 
            sağlığını korumak için geniş kapsamlı veteriner hizmetleri sunuyoruz.
          </p>
        </motion.div>

        {/* Featured Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {featuredServices.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            className="group"
          >
            <motion.div 
              className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden"
              whileHover={{ 
                y: -8, 
                scale: 1.02,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
              }}
              transition={{ duration: 0.3 }}
            >
              {/* Full-card clickable overlay */}
              <Link to={service.href} aria-label={`${service.title} detay sayfası`} className="absolute inset-0 z-10" />
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
              
              {/* Floating Icon */}
              <motion.div 
                className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.color} text-white mb-6`}
                whileHover={{ 
                  rotate: 360,
                  scale: 1.1,
                }}
                transition={{ duration: 0.6 }}
              >
                <service.icon className="w-8 h-8" />
              </motion.div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={service.href}
                  className="relative z-20 inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold group/link"
                >
                  <span>Detayları Gör</span>
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-200" />
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Clinic Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {clinicFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="flex items-center space-x-4"
              >
                <div className="p-3 bg-primary-100 rounded-xl">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section - Links to Other Pages */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-12"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Diğer Hizmetlerimizi Keşfedin
            </h3>
            <p className="text-gray-600">
              Kliniğimiz ve sunduğumuz kapsamlı hizmetler hakkında daha fazla bilgi alın
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Kliniğimiz',
                description: 'Modern donanım, steril ortam ve uzman kadromuzla hizmet veriyoruz',
                icon: '🏥',
                href: '/klinik',
                color: 'from-blue-500 to-blue-600'
              },
              {
                title: 'Hastalık Rehberi',
                description: 'Kedi ve köpek hastalıkları hakkında detaylı bilgiler',
                icon: '📚',
                href: '/hastaliklari',
                color: 'from-green-500 to-green-600'
              },
              {
                title: 'Can Dostlarımız',
                description: 'Farklı kedi ve köpek türleri hakkında bilgiler',
                icon: '🐱🐶',
                href: '/pets',
                color: 'from-purple-500 to-purple-600'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="group"
              >
                <Link
                  to={item.href}
                  className="block p-6 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  <div className="text-center">
                    <div className="text-4xl mb-4">{item.icon}</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {item.description}
                    </p>
                    <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r ${item.color} text-white font-medium text-sm group-hover:shadow-lg transition-all duration-300`}>
                      <span>Detayları Gör</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ServicesSection
